// frontend/src/app/project-corpus/adapters/in-memory-project-corpus.ts
import { Injectable } from '@angular/core';
import { Observable, defer, of, throwError } from 'rxjs';
import { derivePaperStatus } from '../domain/paper-status';
import {
  CreateImportJobRequest,
  FailedUpload,
  GroupCorpusPaper,
  ImportJob,
  ImportSizeEstimate,
  ImportSourceType,
  Page,
  PageRequest,
  PaperItemStatus,
  PaperListFilter,
  PaperMetadataPatch,
  ProjectPaper,
  ProjectSummary,
} from '../domain/types';
import { ProjectCorpusPort } from '../ports/project-corpus.port';

const ATTENTION_STATUSES: PaperItemStatus[] = ['NEEDS_METADATA'];

/** Default demo project id used by `seedDefault()`. */
const DEMO_PROJECT_ID = 'p1';
const DEMO_GROUP_ID = 'g1';

/**
 * In-memory implementation of `ProjectCorpusPort`. This is the LIVE adapter
 * wired into the app for issue #61 — the backend `/imports` and metadata-edit
 * endpoints are not yet available. Behaviour:
 *
 * - Holds in-memory `ImportJob[]`, `ProjectPaper[]`, `GroupCorpusPaper[]`,
 *   and one demo `ProjectSummary` per project.
 * - `createImportJob` immediately stores a `RUNNING` job and schedules a
 *   `setTimeout` (3–6s random) that flips its status to `COMPLETED` and
 *   appends N fake papers whose status is derived from their generated
 *   metadata (COMPLETE, or NEEDS_METADATA when a field such as the abstract
 *   is empty). N depends on the source.
 * - `simulateJobsComplete()` fires any pending completion timers immediately
 *   (used by tests + the integration smoke test).
 * - `listPapers` does pagination + filter + sort in memory.
 * - All reads use `defer(() => of(...))` so each subscribe re-snapshots
 *   state, mirroring how the HTTP adapter would behave.
 */
@Injectable()
export class InMemoryProjectCorpus implements ProjectCorpusPort {
  private jobs: ImportJob[] = [];
  private papers: ProjectPaper[] = [];
  private groupPapers: GroupCorpusPaper[] = [];
  private summaries = new Map<string, ProjectSummary>();
  private nextId = 1;
  private readonly completionTasks = new Map<string, () => void>();
  private readonly completionTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Seeds a small demo state useful for both tests and the live demo.
   * When called without a projectId, uses the default demo project id `p1`.
   * The component layer passes the actual route project id so the seeded data
   * is visible.
   */
  seedDefault(projectId?: string): void {
    this.reset();
    projectId = projectId ?? DEMO_PROJECT_ID;
    const baseDate = Date.parse('2026-05-01T10:00:00Z');

    // One past SUCCEEDED OpenAlex job that brought in ~6 papers
    const openalexJob: ImportJob = {
      id: this.makeId('job'),
      projectId,
      source: 'OPENALEX',
      status: 'COMPLETED',
      startedAt: new Date(baseDate).toISOString(),
      finishedAt: new Date(baseDate + 60_000).toISOString(),
      actorDisplayName: 'Lukas',
      startedByUserId: '',
      itemsAdded: 6,
      itemsNeedingAttention: 1,
      descriptor: 'vector search recommender systems',
      papersTotal: 6,
      papersComplete: 6,
      papersSkipped: 0,
      papersMalformed: 0,
      workTypes: [],
      sourceTypes: [],
      title: null,
      failureReason: null,
      papersIncompleteRemoved: 0,
      papersUndone: 0,
    };
    this.papers.push(...this.makeFakePapers(projectId, openalexJob, 6));

    // One BibTeX job that brought 3 papers
    const bibtexJob: ImportJob = {
      id: this.makeId('job'),
      projectId,
      source: 'BIBTEX',
      status: 'COMPLETED',
      startedAt: new Date(baseDate + 2 * 86_400_000).toISOString(),
      finishedAt: new Date(baseDate + 2 * 86_400_000 + 30_000).toISOString(),
      actorDisplayName: 'Demo User',
      startedByUserId: '',
      itemsAdded: 3,
      itemsNeedingAttention: 1,
      descriptor: 'refs.bib',
      papersTotal: 3,
      papersComplete: 3,
      papersSkipped: 0,
      papersMalformed: 0,
      workTypes: [],
      sourceTypes: [],
      title: null,
      failureReason: null,
      papersIncompleteRemoved: 0,
      papersUndone: 0,
    };
    this.jobs.push(bibtexJob);
    this.papers.push(...this.makeFakePapers(projectId, bibtexJob, 3));

    // Group corpus pool — 12 fake papers, half already in the project
    for (let i = 0; i < 12; i++) {
      this.groupPapers.push({
        id: `gp-${i + 1}`,
        title: `Group corpus paper ${i + 1}`,
        authors: [`Author ${i + 1}`],
        year: 2020 + (i % 6),
        doi: `10.1000/gp.${i + 1}`,
        alreadyInProject: i < 4,
      });
    }

    this.refreshSummary(projectId);
  }

  /**
   * Flush every pending RUNNING-to-SUCCEEDED timer immediately. Used by tests
   * and the integration smoke test to avoid waiting on real wall-clock time.
   */
  simulateJobsComplete(): void {
    const ids = Array.from(this.completionTasks.keys());
    for (const id of ids) {
      const task = this.completionTasks.get(id);
      const timer = this.completionTimers.get(id);
      if (timer) {
        clearTimeout(timer);
        this.completionTimers.delete(id);
      }
      this.completionTasks.delete(id);
      if (task) task();
    }
  }

  /** Wipes every piece of state. */
  reset(): void {
    for (const timer of this.completionTimers.values()) {
      clearTimeout(timer);
    }
    this.completionTimers.clear();
    this.completionTasks.clear();
    this.jobs = [];
    this.papers = [];
    this.groupPapers = [];
    this.summaries.clear();
    this.nextId = 1;
  }

  // ────────────────────────────────────────────────────────────────────────
  //   Reads
  // ────────────────────────────────────────────────────────────────────────

  getProjectSummary(projectId: string): Observable<ProjectSummary> {
    return defer(() => {
      this.refreshSummary(projectId);
      const s = this.summaries.get(projectId);
      return s ? of(s) : throwError(() => new Error(`project ${projectId} not found`));
    });
  }

  listImportJobs(projectId: string, page: PageRequest): Observable<Page<ImportJob>> {
    return defer(() => {
      const all = this.jobs
        .filter((j) => j.projectId === projectId)
        .slice()
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
      return of(this.paginate(all, page));
    });
  }

  listPapers(
    projectId: string,
    filter: PaperListFilter,
    page: PageRequest,
  ): Observable<Page<ProjectPaper>> {
    return defer(() => {
      let filtered = this.papers.filter((p) => p.projectId === projectId);

      if (filter.q && filter.q.trim()) {
        const q = filter.q.trim().toLowerCase();
        filtered = filtered.filter(
          (p) =>
            p.title.toLowerCase().includes(q) ||
            p.authors.some((a) => a.toLowerCase().includes(q)) ||
            (p.doi ?? '').toLowerCase().includes(q),
        );
      }
      if (filter.source) {
        filtered = filtered.filter((p) => p.source === filter.source);
      }
      if (filter.status) {
        if (filter.status === 'NEEDS_ATTENTION') {
          filtered = filtered.filter((p) => ATTENTION_STATUSES.includes(p.status));
        } else {
          filtered = filtered.filter((p) => p.status === filter.status);
        }
      }
      if (filter.importJobId) {
        filtered = filtered.filter((p) => p.importJobId === filter.importJobId);
      }
      if (filter.workType) {
        filtered = filtered.filter((p) => p.workType === filter.workType);
      }
      if (filter.sourceType) {
        filtered = filtered.filter((p) => p.sourceType === filter.sourceType);
      }

      filtered = this.sortPapers(filtered, page);
      return of(this.paginate(filtered, page));
    });
  }

  searchGroupCorpus(
    _groupId: string,
    q: string,
    _projectId: string,
    page: PageRequest,
  ): Observable<Page<GroupCorpusPaper>> {
    return defer(() => {
      let filtered = this.groupPapers.slice();
      if (q && q.trim()) {
        const needle = q.trim().toLowerCase();
        filtered = filtered.filter(
          (p) =>
            p.title.toLowerCase().includes(needle) ||
            p.authors.some((a) => a.toLowerCase().includes(needle)),
        );
      }
      return of(this.paginate(filtered, page));
    });
  }

  previewOpenAlex(
    query: string,
    _workTypes: string[] = [],
    _sourceTypes: string[] = [],
    _yearFrom?: number,
    _yearTo?: number,
    _language?: string[],
  ): Observable<ImportSizeEstimate> {
    return defer(() => {
      const totalCount = Math.max(0, query.length * 13);
      return of<ImportSizeEstimate>({ estimatedCount: totalCount, maxAllowed: 5000 });
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  //   Writes
  // ────────────────────────────────────────────────────────────────────────

  createImportJob(projectId: string, request: CreateImportJobRequest): Observable<ImportJob> {
    return defer(() => {
      const job: ImportJob = {
        id: this.makeId('job'),
        projectId,
        source: request.source,
        status: 'RUNNING',
        startedAt: new Date().toISOString(),
        finishedAt: null,
        actorDisplayName: 'You',
        startedByUserId: '',
        itemsAdded: 0,
        itemsNeedingAttention: 0,
        descriptor: this.describeRequest(request),
        papersTotal: 0,
        papersComplete: 0,
        papersSkipped: 0,
        papersMalformed: 0,
        workTypes: request.source === 'OPENALEX' ? request.workTypes : [],
        sourceTypes: request.source === 'OPENALEX' ? request.sourceTypes : [],
        title: null,
        failureReason: null,
        papersIncompleteRemoved: 0,
        papersUndone: 0,
      };
      this.jobs = [job, ...this.jobs];
      this.refreshSummary(projectId);

      const targetCount = this.targetItemCountFor(request);
      const delayMs = 3000 + Math.floor(Math.random() * 3000); // 3–6s
      const complete = (): void => {
        const fakes = this.makeFakePapers(projectId, job, targetCount);
        this.papers = [...this.papers, ...fakes];
        const needsAttention = fakes.filter(
          (p) => p.status !== 'COMPLETE' && p.status !== 'DUPLICATE_SKIPPED',
        ).length;
        const upgraded: ImportJob = {
          ...job,
          status: 'COMPLETED',
          finishedAt: new Date().toISOString(),
          itemsAdded: fakes.length,
          itemsNeedingAttention: needsAttention,
        };
        this.jobs = this.jobs.map((j) => (j.id === job.id ? upgraded : j));
        this.refreshSummary(projectId);
      };
      this.completionTasks.set(job.id, complete);
      const timer = setTimeout(() => {
        // Only run if not already flushed by simulateJobsComplete()
        if (this.completionTasks.has(job.id)) {
          this.completionTasks.delete(job.id);
          this.completionTimers.delete(job.id);
          complete();
        }
      }, delayMs);
      this.completionTimers.set(job.id, timer);

      return of(job);
    });
  }

  editPaperMetadata(paperId: string, patch: PaperMetadataPatch): Observable<ProjectPaper> {
    return defer(() => {
      const idx = this.papers.findIndex((p) => p.id === paperId);
      if (idx < 0) {
        return throwError(() => new Error(`paper ${paperId} not found`));
      }
      const current = this.papers[idx];
      const updated: ProjectPaper = {
        ...current,
        title: patch.title ?? current.title,
        authors: patch.authors ?? current.authors,
        year: patch.year === undefined ? current.year : patch.year,
        doi: patch.doi === undefined ? current.doi : patch.doi,
        abstractText: patch.abstractText === undefined ? current.abstractText : patch.abstractText,
        venue: patch.venue === undefined ? current.venue : patch.venue,
        workType: patch.workType === undefined ? current.workType : patch.workType,
        sourceType: patch.sourceType === undefined ? current.sourceType : patch.sourceType,
      };
      const recomputed: ProjectPaper = {
        ...updated,
        status:
          updated.status === 'DUPLICATE_SKIPPED'
            ? updated.status
            : derivePaperStatus({
                title: updated.title,
                abstractText: updated.abstractText,
                authors: updated.authors,
                isDuplicate: false,
              }),
      };
      this.papers = this.papers.map((p, i) => (i === idx ? recomputed : p));
      this.refreshSummary(current.projectId);
      return of(recomputed);
    });
  }

  attachPdf(paperId: string, _file: File): Observable<ProjectPaper> {
    return defer(() => {
      const idx = this.papers.findIndex((p) => p.id === paperId);
      if (idx < 0) {
        return throwError(() => new Error(`paper ${paperId} not found`));
      }
      const current = this.papers[idx];
      const nextStatus: PaperItemStatus =
        current.status === 'NEEDS_PDF' ? 'COMPLETE' : current.status;
      const updated: ProjectPaper = { ...current, hasPdf: true, status: nextStatus };
      this.papers = this.papers.map((p, i) => (i === idx ? updated : p));
      this.refreshSummary(current.projectId);
      return of(updated);
    });
  }

  retryExtraction(): Observable<void> {
    return of(void 0);
  }

  advanceToScreening(projectId: string, _force: boolean = false): Observable<void> {
    const existing = this.summaries.get(projectId);
    if (existing) {
      this.summaries.set(projectId, { ...existing, state: 'SCREENING' });
    }
    return of(void 0);
  }

  listFailedUploads(): Observable<FailedUpload[]> {
    return of([]);
  }

  deleteFailedUpload(): Observable<void> {
    return of(void 0);
  }

  updateImportJobTitle(jobId: string, title: string): Observable<ImportJob> {
    return defer(() => {
      const idx = this.jobs.findIndex((j) => j.id === jobId);
      if (idx < 0) {
        return throwError(() => new Error(`import job ${jobId} not found`));
      }
      const updated = { ...this.jobs[idx], title };
      this.jobs = this.jobs.map((j) => (j.id === jobId ? updated : j));
      return of(updated);
    });
  }

  undoImportJob(jobId: string): Observable<void> {
    return defer(() => {
      const idx = this.jobs.findIndex((j) => j.id === jobId);
      if (idx < 0) {
        return throwError(() => new Error(`import job ${jobId} not found`));
      }
      const job = this.jobs[idx];
      if (job.status !== 'COMPLETED') {
        return throwError(() => new Error('Only completed import jobs can be undone'));
      }
      // Remove all papers linked to this job
      const removedPaperIds = new Set<string>();
      this.papers = this.papers.filter((p) => {
        if (p.projectId === job.projectId && p.importJobId === jobId) {
          removedPaperIds.add(p.id);
          return false;
        }
        return true;
      });
      // Mark the job as UNDONE
      this.jobs = this.jobs.map((j) => (j.id === jobId ? { ...j, status: 'UNDONE' as const } : j));
      this.refreshSummary(job.projectId);
      return of(void 0);
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  //   Helpers
  // ────────────────────────────────────────────────────────────────────────

  private describeRequest(request: CreateImportJobRequest): string {
    switch (request.source) {
      case 'OPENALEX':
        return request.query;
      case 'BIBTEX':
        return request.file.name;
      case 'PDF':
        return request.files.length === 1 ? request.files[0].name : `${request.files.length} PDFs`;
      case 'GROUP_CORPUS':
        return `${request.paperIds.length} from group corpus`;
    }
  }

  private targetItemCountFor(request: CreateImportJobRequest): number {
    switch (request.source) {
      case 'PDF':
        return request.files.length;
      case 'OPENALEX':
        // Deterministic-ish from query length; bounded
        return Math.max(1, Math.min(20, request.query.length));
      case 'BIBTEX':
        return 5;
      case 'GROUP_CORPUS':
        return request.paperIds.length;
    }
  }

  private makeFakePapers(projectId: string, job: ImportJob, count: number): ProjectPaper[] {
    const out: ProjectPaper[] = [];
    for (let i = 0; i < count; i++) {
      const isPdfMissing = i % 10 === 9; // keep PDF-missing variety, but it is info-only now
      const abstractText = i % 4 === 0 ? null : 'An abstract.';
      const authors = [`Author ${(i % 7) + 1}`, `Author ${(i % 5) + 2}`];
      const title = `${job.descriptor.slice(0, 40)} — paper ${i + 1}`;
      const status = derivePaperStatus({ title, abstractText, authors, isDuplicate: false });
      const id = this.makeId('p');
      out.push({
        id,
        ptpId: 0,
        projectId,
        title,
        authors,
        year: 2018 + (i % 8),
        venue: i % 3 === 0 ? 'Some Venue' : null,
        doi: `10.1000/${id}`,
        abstractText,
        hasPdf: !isPdfMissing,
        source: job.source,
        status,
        importJobId: job.id,
        addedAt: new Date(Date.parse(job.startedAt) + i * 1000).toISOString(),
        projectsLinkedCount: 1 + (i % 2),
        extractionStatus: null,
        pdfId: null,
        pdfUrl: null,
        workType: null,
        sourceType: null,
        externalId: null,
      });
    }
    return out;
  }

  private sortPapers(items: ProjectPaper[], page: PageRequest): ProjectPaper[] {
    const sort = page.sort;
    if (!sort) {
      // Default: addedAt desc
      return items.slice().sort((a, b) => b.addedAt.localeCompare(a.addedAt));
    }
    const dir = sort.direction === 'asc' ? 1 : -1;
    const sorted = items.slice().sort((a, b) => {
      switch (sort.field) {
        case 'title':
          return a.title.localeCompare(b.title) * dir;
        case 'year':
          return ((a.year ?? 0) - (b.year ?? 0)) * dir;
        case 'firstAuthor': {
          const left = a.authors[0] ?? '';
          const right = b.authors[0] ?? '';
          return left.localeCompare(right) * dir;
        }
        case 'addedAt':
        default:
          return a.addedAt.localeCompare(b.addedAt) * dir;
      }
    });
    return sorted;
  }

  private paginate<T>(items: readonly T[], page: PageRequest): Page<T> {
    const size = Math.max(1, page.size);
    const start = Math.max(0, page.page) * size;
    const slice = items.slice(start, start + size);
    const totalElements = items.length;
    const totalPages = Math.max(1, Math.ceil(totalElements / size));
    return {
      items: slice,
      totalItems: totalElements,
      totalPages,
      currentPage: page.page,
      pageSize: size,
    };
  }

  private refreshSummary(projectId: string): void {
    const projectPapers = this.papers.filter((p) => p.projectId === projectId);
    const perSource: Record<ImportSourceType, number> = {
      OPENALEX: 0,
      BIBTEX: 0,
      PDF: 0,
      GROUP_CORPUS: 0,
    };
    for (const p of projectPapers) {
      perSource[p.source]++;
    }
    const needs = projectPapers.filter((p) => ATTENTION_STATUSES.includes(p.status)).length;
    const existing = this.summaries.get(projectId);
    const summary: ProjectSummary = {
      projectId,
      projectTitle: existing?.projectTitle ?? 'Demo project',
      description:
        existing?.description ?? 'A demo project corpus seeded with sample import jobs and papers.',
      groupId: existing?.groupId ?? DEMO_GROUP_ID,
      state: existing?.state ?? 'INGESTION',
      totalPapers: projectPapers.length,
      perSource,
      itemsNeedingAttention: needs,
    };
    this.summaries.set(projectId, summary);
  }

  private makeId(prefix: string): string {
    return `${prefix}-${this.nextId++}`;
  }
}
